/**
 * src/pages/SafetyPage.js — Safe usage of crackers
 */
import React from 'react';
import { Link } from 'react-router-dom';

const SAFETY_RULES = [
  {
    icon: '🌳',
    color: 'from-emerald-500 to-teal-600',
    title: 'Use in Open Spaces Only',
    points: [
      'Always light crackers in open, well-ventilated areas — parks, open grounds, or wide roads.',
      'Maintain a safe distance from buildings, trees, vehicles, and electric poles.',
      'Never use crackers inside homes, balconies, or enclosed spaces.',
    ],
  },
  {
    icon: '💧',
    color: 'from-blue-500 to-blue-700',
    title: 'Keep Water Nearby',
    points: [
      'Always keep a bucket of water and a fire extinguisher at hand before lighting any cracker.',
      'After use, dip spent crackers in water before disposal to prevent fires.',
      'Water can quickly douse small fires — never leave the area unattended while crackers are burning.',
    ],
  },
  {
    icon: '👨‍👩‍👧',
    color: 'from-orange-500 to-red-600',
    title: 'Adult Supervision for Children',
    points: [
      'Children below 15 years should never handle or light crackers without adult supervision.',
      'Keep sparklers and kids\' items away from the face and clothing — burns can happen instantly.',
      'Educate children on what to do if a cracker misfires: step back and wait, never pick it up.',
    ],
  },
  {
    icon: '🚫',
    color: 'from-red-600 to-red-800',
    title: 'Never Light Multiple Crackers at Once',
    points: [
      'Light one cracker at a time — lighting multiple simultaneously reduces control and increases accident risk.',
      'Keep unlighted crackers stored away from the lighting area to avoid chain ignition.',
      'After lighting, step back immediately to a safe distance before the cracker fires.',
    ],
  },
  {
    icon: '👗',
    color: 'from-purple-500 to-purple-700',
    title: 'Wear Protective Clothing',
    points: [
      'Wear cotton clothes — synthetic fabrics catch fire quickly and can cause serious burns.',
      'Never hold a cracker in your hand while lighting it.',
      'Keep hair tied back and away from flames.',
    ],
  },
  {
    icon: '🏥',
    color: 'from-pink-500 to-red-600',
    title: 'First Aid & Emergency',
    points: [
      'In case of a burn, cool the area with running water for at least 10 minutes — do not apply ice, butter, or oil.',
      'For eye injuries from sparks, rinse with clean water and seek medical attention immediately.',
      'Keep emergency numbers ready: 108 (Ambulance), 101 (Fire Brigade).',
    ],
  },
  {
    icon: '⏰',
    color: 'from-amber-500 to-yellow-600',
    title: 'Follow Government Timings',
    points: [
      'Use crackers only during the permitted hours set by your state government and local authorities.',
      'Be mindful of neighbours — loud crackers late at night cause disturbance and stress.',
      'Check for any local restrictions or bans in your area before celebrating.',
    ],
  },
  {
    icon: '🌿',
    color: 'from-green-500 to-emerald-700',
    title: 'Eco-Friendly Choices',
    points: [
      'Choose green or low-emission crackers whenever possible to reduce air and noise pollution.',
      'Dispose of cracker waste responsibly — collect and dump in designated waste areas.',
      'Avoid using crackers in areas with heavy pollution or on days when AQI is already high.',
    ],
  },
];

const QUICK_DO = [
  'Light one at a time',
  'Stand back after lighting',
  'Keep water bucket ready',
  'Use in open spaces',
  'Adult supervision for kids',
  'Wear cotton clothing',
];

const QUICK_DONT = [
  'Light inside homes',
  'Hold in hand while lighting',
  'Light multiple at once',
  'Let children light alone',
  'Pick up misfired crackers',
  'Use near vehicles or trees',
];

export default function SafetyPage() {
  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'Fireworks Safety Guidelines — Lakshmi Crackers';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Important safety guidelines for using fireworks and crackers. Lakshmi Crackers promotes safe and responsible celebration. Follow Supreme Court regulations.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#fffaf0' }}>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-red-950 via-red-900 to-red-800 overflow-hidden">
        <div className="absolute inset-0 deco-stripes opacity-30 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="text-5xl mb-4">🛡️</div>
          <h1 className="text-5xl sm:text-6xl font-black text-white mb-4"
            style={{ fontFamily: '"Playfair Display",serif' }}>
            Safety First
          </h1>
          <p className="text-red-100/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Every celebration deserves to be joyful <em>and</em> safe.
            Follow these simple guidelines to protect yourself, your family, and your community.
          </p>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8" viewBox="0 0 1440 32" fill="none" preserveAspectRatio="none">
          <path d="M0,20 C480,40 960,0 1440,20 L1440,32 L0,32 Z" fill="#fffaf0" />
        </svg>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">

        {/* Quick Do / Don't */}
        <div className="grid sm:grid-cols-2 gap-5">
          {/* Do */}
          <div className="bg-white rounded-3xl border border-green-200/60 shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600
                flex items-center justify-center text-xl shadow-md">
                ✅
              </div>
              <h3 className="text-xl font-black text-gray-900" style={{ fontFamily: '"Playfair Display",serif' }}>
                Always Do
              </h3>
            </div>
            <ul className="space-y-2.5">
              {QUICK_DO.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-gray-700 font-semibold">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center
                    text-green-600 font-black text-xs flex-shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Don't */}
          <div className="bg-white rounded-3xl border border-red-200/60 shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-red-700
                flex items-center justify-center text-xl shadow-md">
                ❌
              </div>
              <h3 className="text-xl font-black text-gray-900" style={{ fontFamily: '"Playfair Display",serif' }}>
                Never Do
              </h3>
            </div>
            <ul className="space-y-2.5">
              {QUICK_DONT.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-gray-700 font-semibold">
                  <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center
                    text-red-600 font-black text-xs flex-shrink-0">✕</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Detailed rules grid */}
        <div>
          <div className="text-center mb-10">
            <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-2">Detailed Guidelines</p>
            <h2 className="text-3xl font-black text-gray-900" style={{ fontFamily: '"Playfair Display",serif' }}>
              8 Essential Safety Rules
            </h2>
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-3" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {SAFETY_RULES.map((rule) => (
              <div key={rule.title}
                className="bg-white rounded-2xl border border-gray-100 p-6
                  shadow-[0_2px_12px_rgba(0,0,0,0.05)]
                  hover:shadow-[0_12px_32px_rgba(192,21,42,0.09)] hover:-translate-y-0.5
                  transition-all duration-300 group">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${rule.color}
                    flex items-center justify-center text-2xl shadow-md flex-shrink-0
                    group-hover:scale-110 transition-transform duration-300`}>
                    {rule.icon}
                  </div>
                  <h3 className="font-black text-gray-900 text-base leading-tight"
                    style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                    {rule.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {rule.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency numbers */}
        <div className="bg-gradient-to-br from-red-950 to-red-900 rounded-3xl p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 deco-stripes opacity-20 pointer-events-none" />
          <div className="relative">
            <p className="text-yellow-300 font-black text-xs uppercase tracking-widest mb-3">Emergency Contacts</p>
            <h3 className="text-2xl font-black text-white mb-6" style={{ fontFamily: '"Playfair Display",serif' }}>
              Keep These Numbers Handy
            </h3>
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {[
                { num: '108', label: 'Ambulance' },
                { num: '101', label: 'Fire Brigade' },
                { num: '100', label: 'Police' },
              ].map((e) => (
                <div key={e.num} className="bg-white/10 border border-white/15 rounded-2xl py-4 px-3">
                  <p className="text-yellow-300 font-black text-2xl leading-none mb-1"
                    style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                    {e.num}
                  </p>
                  <p className="text-red-200/70 text-xs font-semibold">{e.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-gray-500 text-sm mb-5">Celebrate safely — shop our certified, quality-checked products.</p>
          <Link to="/products"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-red-600 to-red-800
              text-white font-black px-8 py-4 rounded-2xl shadow-xl shadow-red-600/30
              hover:shadow-red-600/50 hover:-translate-y-0.5 transition-all duration-200">
            🎆 Browse Safe Products
          </Link>
        </div>
      </div>
    </div>
  );
}
